<template>
    <!-- Header with reload button -->
    <div class="row items-center justify-between q-px-sm q-py-xs">
        <div class="text-subtitle2">Notifications</div>
        <q-btn
            flat
            round
            dense
            icon="refresh"
            size="sm"
            :loading="refreshing"
            :disable="refreshing"
            @click="refreshNotifications"
        >
            <q-tooltip anchor="bottom left" self="top left">
                Reload
            </q-tooltip>
        </q-btn>
    </div>
    <!-- Loading if !notifications -->
     <!-- place in the center  -->
    <q-spinner
        v-if="notifications == null"
        class="q-mt-md q-mb-md"
        size="50px"
        color="primary"
        style="margin-left: 50%; margin-top: 50%;"
    ></q-spinner>
    <q-list ref="listEl" bordered padding >
        <q-item
            v-if="notifications?.length === 0"
        >
            <q-item-section>
                <q-item-label class="text-center">
                    No notifications
                </q-item-label>
            </q-item-section>
        </q-item>
        <q-item
            v-for="notification in notifications"
            :key="notification.id"
            clickable
            @click="clickNotification(notification)"
            :class="{ 'unread': !notification.seen }"
        >
            <q-item-section avatar>
                <!-- <div class="blue-dot" v-if="!notification.seen"></div> -->
                <!-- Align center vertically -->
                <q-spinner
                    v-if="statusFor(notification).animated"
                    :color="statusFor(notification).color || 'primary'"
                    size="2em"
                    class="q-mr-sm"
                    style="margin-bottom: 5px;"
                />
                <q-icon
                    v-else
                    :name="statusFor(notification).icon || notification.icon || 'notifications'"
                    size="2em"
                    class="q-mr-sm"
                    :style="{
                        'margin-bottom': '5px',
                        color: statusFor(notification).color
                            || (notification.seen ? 'grey' : 'black'), // Quasar primary blue
                    }"
                />
            </q-item-section>
    
            <q-item-section>
                <!-- Icon -->
                
                <q-item-label :class="{ 'text-bold': !notification.seen }">
                    {{ notification.name }}
                </q-item-label>
                <q-item-label
                    caption
                    class="text-small"
                    :class="notification.status === 'error' ? 'text-negative' : 'text-grey-7'"
                >
                    {{ notification.message }}
                </q-item-label>
                <!-- Make text italic -->
                <q-item-label caption class="text-grey-6 text-small italic">
                    {{ formatDate(notification.created_at) }}
                </q-item-label>
            </q-item-section>
    
            <q-item-section side class="column items-end justify-between">
            <q-btn
                flat
                round
                icon="visibility"
                size="sm"
                @click.stop="markAsSeen({ notificationIds: [notification.id] })"
                v-if="!notification.seen"
            >
                <q-tooltip anchor="bottom left" self="top left">
                    Mark as seen
                </q-tooltip>
            </q-btn>
            <template v-else>
                <div class="row q-gutter-sm">
                    <q-btn
                        flat
                        round
                        icon="visibility_off"
                        size="sm"
                        @click.stop="markAsUnseen({ notificationIds: [notification.id] })"
                    >
                        <q-tooltip anchor="bottom left" self="top left" >
                            Mark as unseen
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        flat
                        round
                        icon="clear"
                        size="sm"
                        @click.stop="hideNotifications({ notificationIds: [notification.id] })"
                    >
                        <q-tooltip anchor="bottom left" self="top left" >
                            Delete Notification
                        </q-tooltip>
                    </q-btn>
                </div>
            </template>
               
            </q-item-section>
        </q-item>
    </q-list>
</template>
  
  
<script>
import { mapGetters, mapActions } from "vuex";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Auth } from '@/database';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export default {
name: "NotificationsComp",
    props: {
        module: {
            type: String,
            optional: true,
        },
        context: {
            type: Object,
            optional: true,
        },
    },
    data() {
        return {
            moduleNotifications: null,
            refreshing: false,
            // Map of status -> display config. Treat statuses as an open set:
            // unknown values fall back to `statusDefault`.
            statusConfig: {
                default: { icon: null, color: null, animated: false },
                loading: { icon: null, color: 'grey-6', animated: true },
                processing: { icon: null, color: 'grey-6', animated: true },
                success: { icon: 'check_circle', color: 'positive', animated: false },
                finished: { icon: 'check_circle', color: 'positive', animated: false },
                error: { icon: 'error', color: 'negative', animated: false },
            },
            statusDefault: { icon: null, color: null, animated: false },
        };
    },
    computed: {
        ...mapGetters({
            allNotifications: 'allNotifications', // from Vuex store
        }),
        notifications() {
            if (this.module) {
                return this.moduleNotifications;
            } else {
                return this.allNotifications;
            }
        },
        num_notifications(){
            return (this.notifications || []).filter(n=> !n.seen).length
        }
    },
    methods: {
        ...mapActions({
            markAsSeen: 'markAsSeen', // from Vuex store
            getNotificationHistory: 'getNotificationHistory',
            connectWebSocket: 'connectWebSocket',
            hideNotifications: 'hideNotifications',
            markAsUnseen: 'markAsUnseen',

        }),
        statusFor(notification) {
            return this.statusConfig[notification?.status] || this.statusDefault;
        },
        async refreshNotifications() {
            if (this.refreshing) return; // avoid double-calls
            this.refreshing = true;
            // Preserve scroll position of the list while it re-renders.
            const scrollTarget = this.$refs.listEl?.$el;
            const scrollTop = scrollTarget?.scrollTop ?? 0;
            try {
                if (this.module) {
                    const auth = new Auth();
                    const response = await auth.fetch('/notifications/history?module=' + this.module);
                    if (response.ok) {
                        this.moduleNotifications = await response.json();
                    } else {
                        const errorText = await response.text();
                        console.error("Failed to fetch notification history:", errorText);
                    }
                } else {
                    await this.getNotificationHistory();
                }
                await this.$nextTick();
                if (scrollTarget) scrollTarget.scrollTop = scrollTop;
            } finally {
                this.refreshing = false;
            }
        },
        async clickNotification(notification) {
            // Mark as Seen
            await this.markAsSeen({ notificationIds: [notification.id] });
            // Redirect to the notification URL
            if (notification.url) {
                this.$router.push(notification.url);
            }

        },
        hideNotification(notificationId) {
            this.$store.dispatch("hideNotification", notificationId);
        },
        formatDate(dateString) {
            const date = dayjs.utc(dateString).tz(dayjs.tz.guess());
            const now = dayjs();
            const diffDays = now.diff(date, "day");

            if (diffDays < 7) {
                return date.format("MMM D, YYYY - hh:mm A") + " (" + date.fromNow() + ")"; // e.g. "Sep 3, 2023 - 02:30 PM (2 days ago)"
            } else {
                return date.format("MMM D, YYYY - hh:mm A"); // e.g. "Sep 3, 2023 - 02:30 PM"
            }
        },
    },
    async created() {
        // Fetch notifications when the component is created

        if (this.module) {
            const auth = new Auth();
            const response = await auth.fetch('/notifications/history?module=' + this.module)
            console.log(response)
            if (response.ok) {
                this.moduleNotifications = await response.json();
            } else {
                const errorText = await response.text();
                console.error("Failed to fetch notification history:", errorText);
            }
        } else {
            this.getNotificationHistory();
        }
    },
    watch: {
        notifications: {
            handler(newVal) {
                // id="notification-count" 
                const el = window.parent.document.getElementById("notification-count")
                if (el) {
                    const num_notifications = newVal.filter(n => !n.seen).length;
                    el.innerText = num_notifications;
                    //  Set the hidden attribute to true if there are no notifications
                    el.hidden = false;
                    if (num_notifications > 0) el.hidden = false;
                    else el.hidden = true;
                }

            },
            deep: true,
        },
    }
};
</script>

<style scoped>
.unread {
  background-color: #f0f8ff; /* light blue background for unread */
}

.text-bold {
  font-weight: bold;
}

.blue-dot {
  width: 10px;
  height: 10px;
  background-color: #2196f3; /* Quasar primary blue */
  border-radius: 50%;
  margin-top: 8px;
}
</style>
